import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DataGrid, KpiCard, Spinner,
  type DataGridColumn,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  ChevronRight, ClipboardList, IndianRupee, PackageCheck, Plus, Printer, RefreshCw, Share2, TimerReset, Upload,
} from 'lucide-react';
import { printRegistryDoc } from '../../print/registryDocPrint';

/**
 * Specialized list view for PurchaseOrder. KPIs surface what an
 * operations manager actually wants to see: open POs, pending GRN,
 * total committed spend, and overdue receipts.
 */

interface PORow extends Record<string, unknown> {
  id?: number | string;
  purchaseOrderId?: string;
  orderNumber?: string;
  orderDate?: string;
  expectedDeliveryDate?: string;
  supplierName?: string;
  grandTotal?: number;
  orderStatus?: string;
}

const pickArr = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};

export default function PurchaseOrderList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PORow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      // Resolve supplier names from supplierId (PurchaseOrder has no
      // supplierName column) and read the REAL columns (orderNumber/
      // orderDate/expectedDeliveryDate/grandTotal/orderStatus).
      const [poRes, supRes] = await Promise.all([
        apiClient.get('/purchase/PurchaseOrder'),
        apiClient.get('/purchase/Supplier').catch(() => ({ data: [] })),
      ]);
      const supMap = new Map<string, string>();
      for (const s of pickArr(supRes.data)) supMap.set(String(s.id ?? s.code), String(s.name ?? s.code ?? ''));
      const list: PORow[] = pickArr(poRes.data).map((r) => ({
        id: r.id as number, purchaseOrderId: r.purchaseOrderId as string, code: r.code,
        // keep supplierId + money columns so the branded print can resolve the
        // full vendor block + totals.
        supplierId: r.supplierId, subTotal: r.subTotal, taxAmount: r.taxAmount,
        discountAmount: r.discountAmount, shippingCharge: r.shippingCharge,
        buyerPoNumber: r.buyerPoNumber, transporterName: r.transporterName,
        orderNumber: String(r.orderNumber ?? (r as { poNumber?: unknown }).poNumber ?? r.code ?? r.id ?? ''),
        orderDate: (r.orderDate ?? (r as { poDate?: unknown }).poDate) as string | undefined,
        expectedDeliveryDate: (r.expectedDeliveryDate ?? (r as { expectedDate?: unknown }).expectedDate) as string | undefined,
        supplierName: String(r.supplierName ?? supMap.get(String(r.supplierId)) ?? (r.supplierId != null ? `#${r.supplierId}` : '')),
        grandTotal: Number(r.grandTotal ?? (r as { totalAmount?: unknown }).totalAmount ?? 0),
        orderStatus: String(r.orderStatus ?? (typeof r.status === 'string' ? r.status : '')),
      }));
      // Newest first — by id desc (creation order) so the latest PO is on top.
      list.sort((a, b) => Number(b.id ?? 0) - Number(a.id ?? 0));
      setRows(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Print the PO through the shared branded layout (company header + logo/
  // monogram, QR, signatory, bank) — same path the registry grid uses.
  async function handlePrint(r: PORow) {
    try { await printRegistryDoc('PurchaseOrder', r); }
    catch { setErr('Could not open the print view.'); }
  }
  async function handleShare(r: PORow) {
    const text = `Purchase Order ${r.orderNumber}\nSupplier: ${r.supplierName ?? ''}\nAmount: ₹${inr(Number(r.grandTotal ?? 0))}`;
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: `PO ${r.orderNumber}`, text });
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setErr(null);
      }
    } catch { /* user dismissed the share sheet */ }
  }

  const kpis = useMemo(() => {
    const total = rows.length;
    const totalSpend = rows.reduce((s, r) => s + Number(r.grandTotal ?? 0), 0);
    const open = rows.filter((r) => {
      const s = String(r.orderStatus ?? '').toLowerCase();
      return s === 'posted' || s === 'open' || s === 'sent' || s === 'partially received';
    }).length;
    const overdue = rows.filter((r) => {
      if (!r.expectedDeliveryDate) return false;
      const due = Date.parse(String(r.expectedDeliveryDate));
      const isOverdue = !Number.isNaN(due) && due < Date.now();
      const s = String(r.orderStatus ?? '').toLowerCase();
      return isOverdue && s !== 'received' && s !== 'closed' && s !== 'cancelled';
    }).length;
    return { total, totalSpend, open, overdue };
  }, [rows]);

  const columns = useMemo<DataGridColumn<PORow>[]>(
    () => [
      { accessor: 'orderNumber', title: 'PO #', sortable: true, filterable: true,
        render: (r) => (
          <Link
            to={`/panel/purchase/orders/${encodeURIComponent(String(r.purchaseOrderId ?? r.id ?? ''))}`}
            className="font-medium text-primary hover:underline"
          >
            {String(r.orderNumber ?? r.id ?? '—')}
          </Link>
        )
      },
      { accessor: 'orderDate', title: 'PO date', sortable: true,
        render: (r) => fmtDate(r.orderDate) },
      { accessor: 'supplierName', title: 'Supplier', sortable: true, filterable: true },
      { accessor: 'expectedDeliveryDate', title: 'Expected', sortable: true,
        render: (r) => fmtDate(r.expectedDeliveryDate) },
      { accessor: 'grandTotal', title: 'Amount', sortable: true,
        render: (r) => `₹ ${inr(Number(r.grandTotal ?? 0))}` },
      { accessor: 'orderStatus', title: 'Status', sortable: true, filterable: true,
        render: (r) => <StatusPill v={String(r.orderStatus ?? '')} /> },
      { accessor: '__actions' as never, title: '', width: 110,
        render: (r) => (
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); void handlePrint(r); }}
              className="text-content-muted hover:text-primary"
              title="Print purchase order"
            >
              <Printer size={15} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); void handleShare(r); }}
              className="text-content-muted hover:text-primary"
              title="Share purchase order"
            >
              <Share2 size={15} />
            </button>
            <Link
              to={`/panel/purchase/orders/${encodeURIComponent(String(r.purchaseOrderId ?? r.id ?? ''))}`}
              className="text-content-muted hover:text-primary"
              title="Open"
            >
              <ChevronRight size={16} />
            </Link>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Purchase Orders</h1>
          <p className="text-xs text-content-muted">
            Procure-to-pay · live data from /api/purchase/PurchaseOrder
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/panel/transactions/ingest')}
          title="Drop a PDF tax invoice — auto-create items + post in one step"
        >
          <Upload className="h-3.5 w-3.5" /> Upload PDF
        </Button>
        <Button size="sm" onClick={() => navigate('/panel/purchase/orders/new')}>
          <Plus className="h-3.5 w-3.5" /> New PO
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Total POs"
          value={kpis.total.toLocaleString()}
          subtitle="All statuses"
          icon={<ClipboardList className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Open POs"
          value={kpis.open.toLocaleString()}
          subtitle="Awaiting receipt"
          icon={<PackageCheck className="h-5 w-5 text-warning" />}
        />
        <KpiCard
          title="Committed spend"
          value={`₹ ${inr(kpis.totalSpend)}`}
          subtitle="Sum of order values"
          icon={<IndianRupee className="h-5 w-5 text-primary" />}
        />
        <KpiCard
          title="Overdue receipts"
          value={kpis.overdue.toLocaleString()}
          subtitle="Past expected date"
          icon={<TimerReset className={`h-5 w-5 ${kpis.overdue ? 'text-danger' : 'text-success'}`} />}
        />
      </div>

      {loading && rows.length === 0 ? (
        <Card>
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Loading purchase orders…
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="p-6 text-sm text-content-muted">
            No purchase orders yet. Click <em>New PO</em> to create your first one.
          </div>
        </Card>
      ) : (
        <DataGrid<PORow>
          data={rows}
          columns={columns}
          rowId={(rows[0]?.purchaseOrderId != null ? 'purchaseOrderId' : 'id') as keyof PORow & string}
          defaultSort={{ columnAccessor: 'id', direction: 'desc' }}
        />
      )}
    </div>
  );
}

function StatusPill({ v }: { v: string }) {
  const s = v.toLowerCase();
  let cls = 'border-content-muted/40 bg-content-muted/10 text-content-muted';
  if (s === 'received' || s === 'closed') cls = 'border-success/40 bg-success/10 text-success';
  else if (s === 'posted' || s === 'open' || s === 'sent') cls = 'border-info/40 bg-info/10 text-info';
  else if (s === 'partially received') cls = 'border-warning/40 bg-warning/10 text-warning';
  else if (s === 'overdue') cls = 'border-danger/40 bg-danger/10 text-danger';
  else if (s === 'draft') cls = 'border-warning/40 bg-warning/10 text-warning';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] capitalize ${cls}`}>
      {v || '—'}
    </span>
  );
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
